<script setup lang="ts">
import {onBeforeUnmount, ref, watch} from "vue";
import debounce from "lodash.debounce";
import {Loader2, Search} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {laboratoryApi} from "@/container";
import {BookingLinkLaboratory} from "@/agenda/lib/bookingLink";

defineProps<{
    /** Laboratories the team is already referent for, offered as shortcuts. */
    suggestions: BookingLinkLaboratory[];
}>();

const laboratory = defineModel<BookingLinkLaboratory | null>({required: true});

const query = ref('');
const results = ref<BookingLinkLaboratory[]>([]);
const searching = ref(false);
const failed = ref(false);

/** Responses of superseded searches are dropped (see AgendaPatientSearch). */
let latestQuery = '';

const search = debounce(async (keywords: string) => {
    latestQuery = keywords;

    if (keywords.trim().length < 2) {
        results.value = [];
        failed.value = false;
        searching.value = false;

        return;
    }

    searching.value = true;
    failed.value = false;

    try {
        const found = await laboratoryApi().searchDirectory(keywords);

        if (latestQuery !== keywords) {
            return;
        }

        results.value = found;
    } catch {
        if (latestQuery !== keywords) {
            return;
        }

        results.value = [];
        failed.value = true;
    } finally {
        if (latestQuery === keywords) {
            searching.value = false;
        }
    }
}, 350);

watch(query, (keywords) => {
    searching.value = true;
    search(keywords);
});

const pick = (picked: BookingLinkLaboratory | null) => {
    laboratory.value = picked;
    query.value = '';
    results.value = [];
};

onBeforeUnmount(() => {
    search.cancel();
});
</script>

<template>
  <!--
    No disclosure of its own: this picker sits inside the modal's « Paramètres
    supplémentaires » fold, and a second one would bury the field under two
    clicks for no gain.
  -->
  <div class="flex flex-col gap-2">
    <div class="relative">
      <Search class="absolute left-2.5 top-2.5 size-4 text-gray-400" />
      <Input v-model="query" class="pl-8" placeholder="Rechercher un laboratoire…" />
    </div>
    <div v-if="searching" class="flex items-center gap-2 text-sm text-gray-500">
      <Loader2 class="size-4 animate-spin" />
      Recherche…
    </div>
    <p v-else-if="failed" class="text-sm text-gray-500">
      La recherche a échoué. Modifiez le terme pour réessayer.
    </p>
    <div v-else class="flex flex-wrap gap-1.5">
      <Button
        type="button"
        size="sm"
        :variant="laboratory === null ? 'secondary' : 'outline'"
        @click="pick(null)"
      >
        Sans laboratoire
      </Button>
      <Button
        v-for="option in (query.trim().length >= 2 ? results : suggestions)"
        :key="option.id"
        type="button"
        size="sm"
        :variant="laboratory?.id === option.id ? 'secondary' : 'outline'"
        @click="pick(option)"
      >
        {{ option.name }}
      </Button>
      <p
        v-if="query.trim().length >= 2 && !results.length"
        class="py-1 text-sm text-gray-500"
      >
        Aucun laboratoire trouvé.
      </p>
    </div>
    <!--
      Wording follows the habilitation rule: the laboratory's referents are put
      forward, nobody is taken out.
    -->
    <p v-if="laboratory" class="rounded-lg bg-primary-lightest px-3 py-2 text-xs text-gray-700">
      Les référents de ce laboratoire passent en priorité, et le nom de
      <b>{{ laboratory.name }}</b> sera inscrit dans l'agenda à la prise de rendez-vous.
    </p>
  </div>
</template>
